import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import BottomNav from './components/BottomNav';

// Pages
import AccessPage from './pages/AccessPage';
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import CartPage from './pages/CartPage';
import ProfilePage from './pages/ProfilePage';
import PersonalDetailsPage from './pages/PersonalDetailsPage';
import AuctionPage from './pages/AuctionPage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import CharityPage from './pages/CharityPage';
import HouseRulesPage from './pages/HouseRulesPage';
import MyYessPage from './pages/MyYessPage';
import AddItemPage from './pages/AddItemPage';
import AdminPage from './pages/AdminPage';
import NewsPage from './pages/NewsPage';
import ArticlePage from './pages/ArticlePage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
    const { isAdmin, loading } = useAuth();

    if (loading) {
        return null;
    }

    if (!isAdmin) {
        return <Navigate to="/home" replace />;
    }

    return children;
};

// ScrollToTop component - forces scroll to top on every route change
const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        // Multiple scroll reset methods for maximum compatibility
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        // For iOS PWA - delayed scroll reset
        const timer1 = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }, 50);
        
        const timer2 = setTimeout(() => {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 100);
        
        // Extra aggressive reset for PWA
        const timer3 = setTimeout(() => {
            if (window.scrollY > 0) {
                window.scrollTo(0, 0);
            }
        }, 200);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [pathname]);

    return null;
};

// Layout with Bottom Navigation
const AppLayout = ({ children }) => {
    return (
        <>
            {children}
            <BottomNav />
        </>
    );
};

// Main App Routes
const AppRoutes = () => {
    const { isAuthenticated } = useAuth();

    return (
        <Routes>
            {/* Access / Landing */}
            <Route
                path="/"
                element={
                    isAuthenticated ? <Navigate to="/home" replace /> : <AccessPage />
                }
            />

            {/* Login - redirect to access */}
            <Route
                path="/login"
                element={<Navigate to="/" replace />}
            />

            {/* Home */}
            <Route
                path="/home"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <HomePage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Menu */}
            <Route
                path="/menu"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MenuPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Cart (Tickets) */}
            <Route
                path="/cart"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <CartPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* My Yess (Donation Items) */}
            <Route
                path="/my-yess"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MyYessPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Profile */}
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ProfilePage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Personal Details */}
            <Route
                path="/profile/details"
                element={
                    <ProtectedRoute>
                        <PersonalDetailsPage />
                    </ProtectedRoute>
                }
            />

            {/* Auction Events */}
            <Route
                path="/auction"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AuctionPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/auction/:eventId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AuctionPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* News */}
            <Route
                path="/news"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <NewsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/news/:articleId"
                element={
                    <ProtectedRoute>
                        <ArticlePage />
                    </ProtectedRoute>
                }
            />

            {/* Gallery / Yess Items */}
            <Route
                path="/gallery"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <GalleryPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/gallery/:itemId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <GalleryPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* About */}
            <Route
                path="/about"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AboutPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Charity */}
            <Route
                path="/charity"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <CharityPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* House Rules */}
            <Route
                path="/house-rules"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <HouseRulesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Add Item */}
            <Route
                path="/add"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AddItemPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />

            {/* Admin */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <AdminPage />
                        </AdminRoute>
                    </ProtectedRoute>
                }
            />

            {/* Legacy redirects */}
            <Route path="/events" element={<Navigate to="/auction" replace />} />
            <Route path="/events/:eventId" element={<Navigate to="/auction" replace />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <BrowserRouter>
            <LanguageProvider>
                <AuthProvider>
                    <ScrollToTop />
                    <div className="App">
                        <AppRoutes />
                        <Toaster
                            position="top-center"
                            toastOptions={{
                                style: {
                                    background: '#FFFFFF',
                                    border: '1px solid #E5E5E5',
                                    color: '#1A1918',
                                    fontFamily: 'Manrope, sans-serif',
                                },
                            }}
                        />
                    </div>
                </AuthProvider>
            </LanguageProvider>
        </BrowserRouter>
    );
}

export default App;

